import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Modal,
  IconButton,
  Pagination,
  InputAdornment,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axiosInstance from "../axios";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import OrderDetails from "./OrderDetails";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";


const OrdersContent = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedPlates, setSelectedPlates] = useState([]);
  const [platesLoading, setPlatesLoading] = useState(false);
  const [platesError, setPlatesError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [assignPlatesOrderId, setAssignPlatesOrderId] = useState(null);
  const [assignPlatesBusinessId, setAssignPlatesBusinessId] = useState(null);
  const [showAssignPlates, setShowAssignPlates] = useState(false);
  const [assignOrderId, setAssignOrderId] = useState(null);
  const [assignBusinessId, setAssignBusinessId] = useState(null);
  const [businessDialogOpen, setBusinessDialogOpen] = useState(false);
  const [businessForm, setBusinessForm] = useState({
    id: "",
    name: "",
    address: "",
    telephone: "",
    email: "",
    website: "",
    place_id: "",
  });
  const [businessLoading, setBusinessLoading] = useState(false);
  const [businessSaving, setBusinessSaving] = useState(false);
  const [businessError, setBusinessError] = useState("");
  const [businessSuccess, setBusinessSuccess] = useState("");
  const navigate =useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {t}= useTranslation();
  

  const fetchOrdersCount = async (keyword = "") => {
    try {
      const payload = {};
      if (keyword && keyword.length >= 4) {
        payload.keyword = keyword;
      }

      const response = await axiosInstance.post(
        "orders.php?method=get_orders_count",
        payload
      );

      const totalOrders = parseInt(response.data.orders_count[0].totalorders);
      const pages = Math.ceil(totalOrders / 10);
      setTotalPages(pages);
    } catch (err) {
      console.log("Failed to fetch orders count");
    }
  };

  const fetchOrders = async (
    page = 1,
    showPaginationLoader = false,
    keyword = ""
  ) => {
    if (showPaginationLoader) {
      setPaginationLoading(true);
    } else {
      setLoading(true);
    }

    try {
      const payload = { page };
      if (keyword && keyword.length >= 4) {
        payload.keyword = keyword;
      }

      const response = await axiosInstance.post(
        "orders.php?method=get_orders",
        payload
      );
      setOrders(response.data.orders);
      setCurrentPage(page);
    } catch (err) {
      setError("Failed to fetch orders. Please try again.");
    } finally {
      if (showPaginationLoader) {
        setPaginationLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
      fetchOrdersCount();
      fetchOrders(1);
  }, []);

  const handlePageChange = (event, value) => {
    if (isSearching && searchTerm.length >= 4) {
      fetchOrders(value, true, searchTerm);
    } else {
      fetchOrders(value, true);
    }
  };

  const handleSearchChange = async (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.length >= 4) {
      setIsSearching(true);
      setPaginationLoading(true);
      await fetchOrdersCount(value);
      await fetchOrders(1, true, value);
      setPaginationLoading(false);
    } else if (value.length === 0) {
      setIsSearching(false);
      setPaginationLoading(true);
      await fetchOrdersCount();
      await fetchOrders(1, true);
      setPaginationLoading(false);
    }
  };

  // Fetch plates when opening modal
  const handleOpenModal = async (orderId, businessId) => {
    setOpenModal(true);
    setPlatesLoading(true);
    setPlatesError(null);

    try {
      const response = await axiosInstance.post(
        "orders.php?method=order_plates",
        {
          order_id: orderId,
        }
      );
      setSelectedPlates(response.data.plates || []);
      setAssignPlatesOrderId(orderId);
      setAssignPlatesBusinessId(businessId);
    } catch (error) {
      setPlatesError("Failed to load plates.");
    } finally {
      setPlatesLoading(false);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedPlates([]);
  };

  const refreshCurrentOrders = async () => {
    if (isSearching && searchTerm.length >= 4) {
      await fetchOrders(currentPage, true, searchTerm);
      return;
    }

    await fetchOrders(currentPage, true);
  };

  const handleAssignPlates = (orderId, businessId) => {
    setOpenModal(false);
    setAssignOrderId(orderId);
    setAssignBusinessId(businessId);
    setShowAssignPlates(true);
  };

  const handleBusinessFieldChange = (event) => {
    const { name, value } = event.target;
    setBusinessForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenBusinessDialog = async (order) => {
    const businessId = order.business_id;
    setBusinessDialogOpen(true);
    setBusinessLoading(true);
    setBusinessError("");
    setBusinessSuccess("");
    setBusinessForm({
      id: businessId || "",
      name: order.business_name || "",
      address: "",
      telephone: "",
      email: "",
      website: "",
      place_id: "",
    });

    try {
      const response = await axiosInstance.post(
        "places.php?method=get_place_details",
        { id: businessId }
      );
      const details = response.data?.place_details || {};

      setBusinessForm({
        id: businessId || details.id || "",
        name: details.name || order.business_name || "",
        address: details.address || "",
        telephone: details.telephone || "",
        email: details.email || "",
        website: details.website || "",
        place_id: details.place_id || "",
      });
    } catch (err) {
      setBusinessError("Business details could not be loaded. You can still update the available fields.");
    } finally {
      setBusinessLoading(false);
    }
  };

  const handleCloseBusinessDialog = () => {
    setBusinessDialogOpen(false);
    setBusinessError("");
    setBusinessSuccess("");
  };

  const handleSaveBusiness = async () => {
    if (!businessForm.id) {
      setBusinessError("Missing business id.");
      return;
    }

    if (!businessForm.name.trim()) {
      setBusinessError("Business name is required.");
      return;
    }

    if (
      businessForm.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(businessForm.email.trim())
    ) {
      setBusinessError("Please enter a valid business email.");
      return;
    }

    setBusinessSaving(true);
    setBusinessError("");
    setBusinessSuccess("");

    const payload = {
      id: businessForm.id,
      business_id: businessForm.id,
      name: businessForm.name.trim(),
      address: businessForm.address.trim(),
      telephone: businessForm.telephone.trim(),
      email: businessForm.email.trim(),
      website: businessForm.website.trim(),
      place_id: businessForm.place_id.trim(),
    };

    try {
      const response = await axiosInstance.post(
        "places.php?method=update_place_details",
        payload
      );

      if (response.data?.status && response.data.status !== "success") {
        throw new Error(response.data?.message || "Business update failed.");
      }

      const updatedDetails = response.data?.place_details || payload;
      setBusinessForm((prev) => ({
        ...prev,
        name: updatedDetails.name || payload.name,
        address: updatedDetails.address || payload.address,
        telephone: updatedDetails.telephone || payload.telephone,
        email: updatedDetails.email || payload.email,
        website: updatedDetails.website || payload.website,
        place_id: updatedDetails.place_id || payload.place_id,
      }));
      setBusinessSuccess("Business information updated successfully.");
      await refreshCurrentOrders();
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          String(order.business_id) === String(payload.business_id)
            ? {
                ...order,
                business_name: updatedDetails.name || payload.name,
                business_email: updatedDetails.email || payload.email,
              }
            : order
        )
      );
    } catch (err) {
      setBusinessError(
        err.response?.data?.message ||
          err.message ||
          "Failed to update business information."
      );
    } finally {
      setBusinessSaving(false);
    }
  };

  const handleManualOrderCreation=()=>
  {
    navigate('/dashboard/manualordercreation')
  }

  return (
    <>
      <Box>
        { showAssignPlates ? (
          <OrderDetails
            orderId={assignOrderId}
            businessId={assignBusinessId}
            onDone={() => setShowAssignPlates(false)}
            onAssigned={refreshCurrentOrders}
          />
        ) : (
          <>
            {" "}
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  pb: 2,
                  display: { sm: "flex" },
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {/* small screens */}
                <Box
                  sx={{
                    display: { xs: "flex", sm: "none" },
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: { xs: "20px", sm: "28px" },
                      fontWeight: 600,
                      fontFamily: "Roboto",
                    }}
                  >
                     {t(" Customer Orders")}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleManualOrderCreation()}
                    sx={{
                      backgroundColor: "#2B56F5",
                      "&:hover": {
                        backgroundColor: "#1E3FA3",
                      },
                      fontFamily: "Roboto",
                      textTransform: "capitalize",
                      fontSize: "14px",
                    }}
                  >
                    <AddIcon />
                    {t("Create Order")}
                  </Button>
                </Box>
                <TextField
                  placeholder="Search Your Orders..."
                  size="small"
                  variant="outlined"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{
                    width: { xs: "100%", sm: "500px" },
                    display: { xs: "flex", sm: "none" },
                    pt: "15px",
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {searchTerm.length > 0 ? (
                          <IconButton
                            edge="end"
                            onClick={async () => {
                              setSearchTerm("");
                              setIsSearching(false);
                              setPaginationLoading(true);
                              await fetchOrdersCount();
                              await fetchOrders(1, true);
                              setPaginationLoading(false);
                            }}
                          >
                            <CloseIcon color="error" />
                          </IconButton>
                        ) : (
                          <SearchIcon color="primary" />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />

                {/* large screens */}
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: "20px", sm: "28px" },
                    fontWeight: 600,
                    fontFamily: "Roboto",
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  {t("Customer Orders")}
                </Typography>
                <Box sx={{ display: { xs: "none", sm: "block" } }}>
                  <TextField
                    placeholder="Search Your Orders..."
                    size="small"
                    variant="outlined"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    sx={{ width: { xs: "100%", sm: "500px" } }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {searchTerm.length > 0 ? (
                            <IconButton
                              edge="end"
                              onClick={async () => {
                                setSearchTerm("");
                                setIsSearching(false);
                                setPaginationLoading(true);
                                await fetchOrdersCount();
                                await fetchOrders(1, true);
                                setPaginationLoading(false);
                              }}
                            >
                              <CloseIcon color="error" />
                            </IconButton>
                          ) : (
                            <SearchIcon color="primary" />
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleManualOrderCreation()}
                  sx={{
                    backgroundColor: "#2B56F5",
                    "&:hover": {
                      backgroundColor: "#1E3FA3",
                    },
                    display: { xs: "none", sm: "block" },
                    fontFamily: "Roboto",
                    textTransform: "capitalize",
                    fontSize: "16px",
                  }}
                >
                  <AddIcon />
                  {t("Create Order")}
                </Button>
              </Box>
              <Box>
                {loading ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    p={3}
                    height="75vh"
                  >
                    <CircularProgress />
                  </Box>
                ) : error ? (
                  <Alert severity="error">{error}</Alert>
                ) : orders?.length === 0 ? (
                  <Alert severity="info" sx={{ fontSize: "14px", pt: "20px" }}>
                    {t("No orders found.")}
                  </Alert>
                ) : (
                  <>
                    <TableContainer component={Paper} elevation={2}>
                      <Table sx={{ minWidth: 900 }}>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                            <TableCell
                              sx={{ fontWeight: "600", fontFamily: "Roboto" }}
                            >
                              {t("Order ID")}
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "600", fontFamily: "Roboto" }}
                            >
                             {t("Date")}
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "600", fontFamily: "Roboto" }}
                            >
                               {t("Product Name")}
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "600", fontFamily: "Roboto" }}
                            >
                              {t("Customer Details")}
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "600", fontFamily: "Roboto" }}
                            >
                              {t("Business Name")}
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "600", fontFamily: "Roboto" }}
                            >
                              {t("Business Email")}
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "600", fontFamily: "Roboto" }}
                            >
                              {t("Transaction Details")}
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "600", fontFamily: "Roboto" }}
                            ></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {orders?.map((order) => (
                            <TableRow
                              key={order.id}
                              sx={{ "&:hover": { backgroundColor: "#d8dadb" } }}
                            >
                              <TableCell>{order.id}</TableCell>
                              <TableCell>
                                {new Date(order.created_at)
                                  .toLocaleString("en-US", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                  })
                                  .replace(",", "")}
                              </TableCell>
                              <TableCell>{order.pname}</TableCell>
                              <TableCell>
                                <div>
                                  <div>{order.username}</div>
                                  <div
                                    style={{
                                      fontSize: "0.90em",
                                      color: "var(--pg-text-muted)",
                                    }}
                                  >
                                    {order.email}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div>
                                  {order.business_name}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div
                                  style={{
                                    fontSize: "0.90em",
                                    color: "var(--pg-text-muted)",
                                    wordBreak: "break-word",
                                    maxWidth: 220,
                                  }}
                                >
                                  {order.business_email || ""}
                                </div>
                              </TableCell>

                              <TableCell>
                                <div>
                                  <div>${order.amount}</div>
                                  <div
                                    style={{
                                      fontSize: "0.90em",
                                      color: "var(--pg-text-muted)",
                                    }}
                                  >
                                    {order.transaction_id}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    flexWrap: "wrap",
                                  }}
                                >
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  onClick={() =>
                                    handleOpenModal(order.id, order.business_id)
                                  }
                                  sx={{
                                    backgroundColor: "#2B56F5",
                                    "&:hover": {
                                      backgroundColor: "#1E3FA3",
                                    },
                                    textTransform: "capitalize",
                                    fontSize: "14px",
                                  }}
                                >
                                  {t("View Genie Tags")}
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                  onClick={() =>
                                    handleAssignPlates(
                                      order.id,
                                      order.business_id
                                    )
                                  }
                                  sx={{
                                    textTransform: "capitalize",
                                    fontSize: "14px",
                                  }}
                                >
                                  {t("Assign Genie Tags")}
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                  onClick={() => handleOpenBusinessDialog(order)}
                                  sx={{
                                    textTransform: "capitalize",
                                    fontSize: "14px",
                                  }}
                                >
                                  {t("Edit Business")}
                                </Button>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {paginationLoading && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          bgcolor: "rgba(138, 136, 136, 0.6)", // blurred effect
                          backdropFilter: "blur(1px)",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          zIndex: 10,
                          borderRadius: "4px",
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    )}
                    <Box display="flex" justifyContent="center" p={2}>
                      <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        shape="rounded"
                        size={isMobile ? "small" : "large"}
                        sx={{
                          "& .MuiPaginationItem-root": {
                            borderRadius: "50%",
                            width: isMobile ? 30 : 40,
                            height: isMobile ? 30 : 40,
                            minWidth: isMobile ? 30 : 40,
                          },
                        }}
                      />
                    </Box>
                  </>
                )}
              </Box>

              {/* Modal for Viewing Plates */}
              <Modal
                open={openModal}
                onClose={handleCloseModal}
                aria-labelledby="plates-modal"
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "100%",
                    maxWidth: 500,
                    bgcolor: "var(--pg-card)",
                    boxShadow: 24,
                    p: 3,
                    borderRadius: 2,
                    overflowX: "hidden",
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="h6">{t("Your Genie Tags")}</Typography>
                    <IconButton onClick={handleCloseModal}>
                      <CloseIcon />
                    </IconButton>
                  </Box>

                  {platesLoading ? (
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress />
                    </Box>
                  ) : platesError ? (
                    <Alert severity="error">{platesError}</Alert>
                  ) : selectedPlates?.length > 0 ? (
                    <>
                      <TableContainer component={Paper} elevation={2}>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                              <TableCell sx={{ fontWeight: "bold" }}>
                              {t("Business")}
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                              {t("Genie Tag")}
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                              {t("Status")}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedPlates.map((plate, index) => (
                              <TableRow key={index}>
                                <TableCell>{plate.name}</TableCell>
                                <TableCell
                                  sx={{ color: "green", fontWeight: "bold" }}
                                >
                                  {plate.plate_code}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: plate.status === "1" ? "green" : "red",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {plate.status === "1" ? "Active" : "InActive"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() =>
                          handleAssignPlates(
                            assignPlatesOrderId,
                            assignPlatesBusinessId
                          )
                        }
                        sx={{
                          mt: "10px",
                          textTransform: "capitalize",
                          fontSize: "14px",
                          fontFamily: "Roboto",
                        }}
                      >
                        {t("Assign Genie Tags")}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography>{t("No Genie Tags available.")}</Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() =>
                          handleAssignPlates(
                            assignPlatesOrderId,
                            assignPlatesBusinessId
                          )
                        }
                        sx={{
                          backgroundColor: "#2B56F5",
                          "&:hover": {
                            backgroundColor: "#1E3FA3",
                          },
                          mt:"10px",textTransform:"capitalize",
                          fontSize:"14px",fontFamily:"Roboto",letterSpacing:"1px"
                        }}
                      >
                        {t("Assign Genie Tags")}
                      </Button>
                    </>
                  )}
                </Box>
              </Modal>

              <Dialog
                open={businessDialogOpen}
                onClose={handleCloseBusinessDialog}
                fullWidth
                maxWidth="sm"
              >
                <DialogTitle
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontFamily: "Roboto",
                  }}
                >
                  {t("Edit Business Information")}
                  <IconButton onClick={handleCloseBusinessDialog}>
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                  {businessLoading ? (
                    <Box display="flex" justifyContent="center" p={3}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      {businessError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {businessError}
                        </Alert>
                      )}
                      {businessSuccess && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                          {businessSuccess}
                        </Alert>
                      )}
                      <TextField
                        fullWidth
                        label={t("Business Name")}
                        name="name"
                        value={businessForm.name}
                        onChange={handleBusinessFieldChange}
                        margin="normal"
                      />
                      <TextField
                        fullWidth
                        label={t("Address")}
                        name="address"
                        value={businessForm.address}
                        onChange={handleBusinessFieldChange}
                        margin="normal"
                        multiline
                        minRows={2}
                      />
                      <TextField
                        fullWidth
                        label={t("Phone")}
                        name="telephone"
                        value={businessForm.telephone}
                        onChange={handleBusinessFieldChange}
                        margin="normal"
                      />
                      <TextField
                        fullWidth
                        label={t("Email")}
                        name="email"
                        value={businessForm.email}
                        onChange={handleBusinessFieldChange}
                        margin="normal"
                      />
                      <TextField
                        fullWidth
                        label={t("Website")}
                        name="website"
                        value={businessForm.website}
                        onChange={handleBusinessFieldChange}
                        margin="normal"
                      />
                      <TextField
                        fullWidth
                        label={t("Google Place ID or Review URL")}
                        name="place_id"
                        value={businessForm.place_id}
                        onChange={handleBusinessFieldChange}
                        margin="normal"
                        helperText={t("Use the exact Google Place ID when autocomplete selects the wrong business.")}
                      />
                    </>
                  )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                  <Button
                    onClick={handleCloseBusinessDialog}
                    sx={{ textTransform: "capitalize" }}
                  >
                    {t("Cancel")}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveBusiness}
                    disabled={businessLoading || businessSaving}
                    sx={{
                      backgroundColor: "#2B56F5",
                      textTransform: "capitalize",
                    }}
                  >
                    {businessSaving ? (
                      <CircularProgress color="inherit" size={22} />
                    ) : (
                      t("Save")
                    )}
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

export default OrdersContent;
